import React, { useEffect } from 'react';
import { getBrandName } from '../utils/brand';

// `e` wraps an endpoint path in <code> for inline display.
const e = path => <code>{path}</code>;

const entries = [
    {
        date: '2026-09-30',
        version: '1.6',
        items: [
            <>Errors now come back with their real HTTP status — {e('400')}, {e('401')}, {e('403')}, {e('404')}, {e('413')}, {e('429')}, {e('500')}, {e('502')} or {e('503')} — instead of {e('200')} with the error in the body. The body is unchanged, and every endpoint documents its error responses.</>,
            <>File uploads: {e('POST /cards/:id/attachment-upload-url')} returns a short-lived upload URL and the plan's file-size limit. PUT the file there, then attach it with {e('POST /cards/:id/attachments')}, which also adds links.</>,
            <>New endpoints: {e('PATCH /attachments/:id')} renames an attachment and {e('DELETE /attachments/:id')} removes it.</>,
            <>{e('GET /cards/:id/attachments')} takes a card number as well as a card ObjectId.</>,
            <>Card history entries include {e('type')} (what happened, e.g. {e('card.moved')}) and {e('field')} (for updates, the attribute that changed). {e('action')} and {e('subAction')} are deprecated and will be removed on 2027-03-31.</>,
        ],
    },
    {
        date: '2026-08-24',
        version: '1.5',
        items: [
            <>New endpoint: {e('GET /cards/:id/attachments')} — a card's attachments, with short-lived signed download links for uploaded files and original links for external ones.</>,
            <>New webhook event: {e('COMMENT_CREATED')}.</>,
            <>{e('customData')} on {e('POST /comments')} — an opaque object echoed into webhook payloads, never interpreted. Use it to pass integration flags through.</>,
            <>Card responses now include {e('url')} — a direct, brand-correct link to open the card in the app.</>,
            <>{e('descriptionDelta')} (Quill Delta) documented as the canonical rich-text format across cards, comments, checklists, and tasks; HTML input remains accepted.</>,
            <>Hosted MCP server: <a className="link" href="/mcp">one-click OAuth sign-in</a> — user-level connections, no API key required.</>,
        ],
    },
    {
        date: '2026-04-30',
        version: '1.4',
        items: [
            <>New canonical flat URLs: {e('POST /checklists')}, {e('POST /tasks')}, {e('POST /comments')}, {e('POST /tokens')}.</>,
            <>New endpoints: {e('PATCH /cards/:id')}, {e('GET /cards/:id/tokens')}, {e('DELETE /tokens/:id')}, {e('GET /boards/:publicId')}.</>,
            <>Full checklist + task lifecycle now exposed externally (update, delete, move).</>,
            <>Old paths ({e('POST /cards/:id/checklists')}, {e('/cards/:id/comments')}, {e('/cards/:id/tokens')}, {e('PUT /cards/:id')}, {e('/board/...')}) marked deprecated. They still work — please migrate.</>,
            <>Downloadable Postman collection added under Tools.</>,
        ],
    },
    {
        date: '2024-09-12',
        version: '1.3',
        items: [
            <>Added {e('/board/:publicId/reports/:reportType')} for board-level analytics.</>,
            <>Added {e('/cards/:id/metrics')} for per-card cycle time data.</>,
        ],
    },
    {
        date: '2023-04-04',
        version: '1.2',
        items: [
            <>Webhooks management endpoints (CRUD).</>,
            <>Card history endpoint added.</>,
        ],
    },
];

const Changelog = () => {
    const brandName = getBrandName();
    useEffect(() => {
        document.title = `Changelog · ${brandName} Docs`;
    }, [brandName]);

    return (
        <div className="swagger-ui doc-page">
            <div className="information-container wrapper">
                <div className="info">
                    <h2 className="title">Changelog</h2>
                    <p>
                        Notable additions to the {brandName} public API. We follow{' '}
                        <a
                            className="link"
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://semver.org/"
                        >
                            semantic versioning
                        </a>{' '}
                        — breaking changes bump the major version and are announced in advance.
                    </p>

                    {entries.map(entry => (
                        <section key={entry.version} className="changelog-entry">
                            <header>
                                <h2>
                                    <span className="changelog-version">v{entry.version}</span>
                                    <time dateTime={entry.date} className="changelog-date">
                                        {new Date(entry.date).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        })}
                                    </time>
                                </h2>
                            </header>
                            <ul className="circle">
                                {entry.items.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Changelog;
